import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { DELIVERY_STATUSES } from '../../../../../utils/catalogs';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { UPSELL_FILTER_OPTIONS } from '../../../../../features/upselling/upsellingConstants';
import { DateRangePicker } from '../../../../../components/ui/DateRangePicker';
import { AdvancedFilter } from '../../../../../components/deliveries/AdvancedFilter';
import { EMPTY_ADVANCED_FILTER, isAdvancedFilterActive } from '../../../../../features/deliveries/advancedFilter';

/**
 * @param {object} props
 * @param {object} props.filters - The slice's `filters`
 * @param {(fieldOrPatch: string|object, value?: *) => void} props.onFilterChange
 * @param {boolean} props.isAdmin
 * @param {boolean} [props.canFilterByDriver] - Only the checkout plan assigns
 *   drivers; on any other one the filter can only return an empty table.
 * @param {boolean} [props.canFilterByUpsell] - Off while upselling is hidden.
 * @param {boolean} [props.canUseAdvancedFilter] - The plan has the
 *   `advanced_filter` addon: a criterion plus a pasted list of values.
 * @param {Array<{value: number, label: string}>} [props.companyOptions] - The
 *   admin's companies, without the "Todos" sentinel, which is prepended here.
 */
export const DeliveryFilters = ({
  filters,
  onFilterChange,
  isAdmin,
  canFilterByDriver = false,
  canFilterByUpsell = false,
  canUseAdvancedFilter = false,
  companyOptions = [],
}) => {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFilterChange('search', searchInput);
      }
    }, 1500); // 1.5s debounce
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, onFilterChange]);

  const statusOptions = useMemo(() => {
    return [
      { value: 'all', label: 'Todos' },
      ...DELIVERY_STATUSES.map(s => ({ value: s.system_name, label: s.user_name }))
    ];
  }, []);

  // The backend compares this against `companies_company.id`, so it has to be
  // an id. It used to be two hardcoded social reasons, which meant picking any
  // company but "Todos" sent a string into an integer column.
  const sellerOptions = [{ value: 'all', label: 'Todos' }, ...companyOptions];

  const driverOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'assigned', label: 'Con conductor' },
    { value: 'not_assigned', label: 'Sin asignar' }
  ];

  // A filter added after someone's filters were already persisted is missing
  // from their stored state, so it is read with a default rather than assumed.
  const upsellFilter = filters.upsell_filter ?? 'all';
  const advancedFilterActive = canUseAdvancedFilter && isAdvancedFilterActive(filters);

  return (
    <div className="flex flex-col md:flex-row flex-wrap items-center gap-4">

      {/* 1. Barra de Búsqueda Principal (Movida al Inicio y Ampliada) */}
      <div className="flex-1 min-w-[300px] flex items-center gap-2 bg-white dark:bg-[#1a1a1a] border border-border rounded-xl px-4 py-2">
        <Search size={16} className="text-text-muted flex-shrink-0" />
        <input
          type="text"
          placeholder="Buscar por orden, tracking, destino..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="bg-transparent text-sm w-full outline-none dark:text-white placeholder:text-text-muted"
        />
      </div>

      <DateRangePicker
        startDate={filters.start_date}
        endDate={filters.end_date}
        onChange={onFilterChange}
      />

      <FilterableSelect
        label="Estado:"
        value={filters.delivery_status}
        onChange={(val) => onFilterChange('delivery_status', val)}
        options={statusOptions}
      />

      {isAdmin && (
        <>
          <FilterableSelect
            label="Seller:"
            value={filters.company}
            onChange={(val) => onFilterChange('company', val)}
            options={sellerOptions}
          />

          {canFilterByDriver && (
            <FilterableSelect
              label="Conductor:"
              value={filters.assignedDriverFilter}
              onChange={(val) => onFilterChange('assignedDriverFilter', val)}
              options={driverOptions}
            />
          )}

          {canFilterByUpsell && (
            <FilterableSelect
              label="Upselling:"
              value={upsellFilter}
              onChange={(val) => onFilterChange('upsell_filter', val)}
              options={UPSELL_FILTER_OPTIONS}
            />
          )}
        </>
      )}

      {canUseAdvancedFilter && (
        <AdvancedFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {/* A hidden filter never counts as active: with the driver select out of
          sight, its value can only be the default, and "Limpiar filtros" would
          be offering to clear something nobody can see. It is still reset
          below, which costs nothing and keeps the payload complete. */}
      {(searchInput.trim() !== '' || filters.company !== 'all' || (canFilterByDriver && filters.assignedDriverFilter !== 'all') || filters.delivery_status !== 'all' || (canFilterByUpsell && upsellFilter !== 'all') || advancedFilterActive) && (
        <button
          onClick={() => {
            setSearchInput('');
            onFilterChange({
              search: '',
              company: 'all',
              assignedDriverFilter: 'all',
              delivery_status: 'all',
              upsell_filter: 'all',
              ...EMPTY_ADVANCED_FILTER
            });
          }}
          className="text-sm font-medium text-text-muted hover:text-ninja-dark dark:hover:text-white transition-colors px-2 ml-auto"
        >
          Limpiar filtros
        </button>
      )}
    </div>
  );
};
